import { LikedItem } from '../../../../src/commonTypes';

export type SynthesiseIdeaInputDisplay = {
	title: string;
	detail?: string;
	context: string;
	imageUrl?: string;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
	typeof value === 'object' && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;

const firstString = (record: Record<string, unknown>, keys: string[]): string | undefined => {
	for (const key of keys) {
		const value = record[key];
		if (typeof value === 'string' && value.trim()) return value.trim();
	}
	return undefined;
};

const humanizeType = (type: string): string =>
	type
		.replace(/[-_]+/g, ' ')
		.replace(/\b\w/g, (character) => character.toUpperCase());

const summarizeArray = (value: unknown): string | undefined => {
	if (!Array.isArray(value)) return undefined;
	const labels = value
		.map((entry) => {
			if (typeof entry === 'string') return entry.trim();
			const record = asRecord(entry);
			return record ? firstString(record, ['name', 'title', 'label', 'description']) : undefined;
		})
		.filter((entry): entry is string => Boolean(entry));
	return labels.length > 0 ? labels.join(', ') : undefined;
};

export const getSynthesiseIdeaInputDisplay = (
	item: LikedItem
): SynthesiseIdeaInputDisplay => {
	const fallbackTitle = item.title?.trim() || humanizeType(item.type);
	const rawText = item.text?.trim() || '';
	if (!rawText) {
		return {
			title: fallbackTitle,
			context: item.title?.trim() || item.url?.trim() || fallbackTitle,
			imageUrl: item.type.includes('image') ? item.url : undefined,
		};
	}

	try {
		const parsed = asRecord(JSON.parse(rawText));
		if (!parsed) throw new Error('Idea data is not an object');
		const theme = firstString(parsed, ['theme']);
		const title =
			firstString(parsed, ['name', 'title', 'group', 'phrase', 'label']) ||
			item.title?.trim() ||
			(theme && !theme.includes('/') ? theme : undefined) ||
			fallbackTitle;
		const detail =
			firstString(parsed, [
				'rationale',
				'biography',
				'bio',
				'description',
				'summary',
				'intro',
				'profession',
			]) ||
			summarizeArray(parsed.values) ||
			summarizeArray(parsed.stakeholders) ||
			summarizeArray(parsed.colours);
		return {
			title,
			detail: detail && detail !== title ? detail : undefined,
			context: rawText,
			imageUrl:
				firstString(parsed, ['imageUrl', 'image', 'src']) ||
				(item.type.includes('image') ? item.url : undefined),
		};
	} catch {
		return {
			title: item.title?.trim() || rawText.split(/\r?\n/, 1)[0] || fallbackTitle,
			context: rawText,
			imageUrl: item.type.includes('image') ? item.url : undefined,
		};
	}
};
